import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { useAuth } from "../contexts/AuthContext";
import { getTeamLeaderboard } from "../utils/firebase";
import CircularGauge from "../components/CircularGauge";


const TROPHY_COLORS = {
  1: "#fbbf24",
  2: "#9ca3af",
  3: "#d97706",
};

export default function LeaderboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [teamName, setTeamName] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadLeaderboard = useCallback(async () => {
    try {
      const result = await getTeamLeaderboard();
      setTeamName(result.data.teamName);
      setLeaderboard(result.data.leaderboard || []);
    } catch (err) {
      console.error("Failed to load leaderboard:", err);
      setError(err.message || t("leaderboardPage.failedLoad"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (leaderboard.length === 0) {
    return (
      <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh" }}>
        <Container maxWidth="lg" sx={{ py: 5 }}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: '"Cal Sans", "Inter", sans-serif',
              fontWeight: 600,
              color: "#111827",
              mb: 2,
            }}
          >
            {t("leaderboardPage.title")}
          </Typography>
          <Typography sx={{ color: "#6b7280" }}>
            {t("leaderboardPage.emptyState")}
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 5, px: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <EmojiEventsIcon sx={{ fontSize: 28, color: "#fbbf24" }} />
          <Typography
            variant="h5"
            sx={{
              fontFamily: '"Cal Sans", "Inter", sans-serif',
              fontWeight: 600,
              color: "#111827",
            }}
          >
            {t("leaderboardPage.title")}
          </Typography>
          {teamName && (
            <Typography sx={{ color: "#6b7280", fontSize: 14 }}>
              — {teamName}
            </Typography>
          )}
        </Box>
        {error && (
          <Typography color="error" sx={{ mb: 2, fontSize: 14 }}>
            {error}
          </Typography>
        )}
        <TableContainer
          component={Paper}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f9fafb" }}>
                <TableCell sx={{ fontWeight: 600, color: "#111827" }}>{t("leaderboardPage.rank")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#111827" }}>{t("leaderboardPage.member")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#111827" }} align="right">
                  {t("leaderboardPage.avgScore")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#111827" }} align="right">
                  {t("leaderboardPage.calls")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaderboard.map((entry) => {
                const isCurrentUser = entry.userId === user?.uid;
                const trophyColor = TROPHY_COLORS[entry.rank];
                return (
                  <TableRow
                    key={entry.userId}
                    sx={{
                      bgcolor: entry.rank <= 3 ? (entry.rank === 1 ? "#fffbeb" : entry.rank === 2 ? "#f9fafb" : "#fffbeb") : "inherit",
                      "&:hover": { bgcolor: "#f9fafb" },
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {entry.rank <= 3 ? (
                          <EmojiEventsIcon sx={{ fontSize: 20, color: trophyColor }} />
                        ) : null}
                        <Typography sx={{ fontWeight: 600, color: "#111827" }}>
                          {entry.rank}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: "#111827", fontWeight: isCurrentUser ? 600 : 400 }}>
                        {entry.email}
                        {isCurrentUser && (
                          <Typography component="span" sx={{ color: "#6b7280", fontSize: 12, ml: 1 }}>
                            {t("leaderboardPage.you")}
                          </Typography>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {entry.avgScore != null ? (
                        <Box display="inline-flex" justifyContent="flex-end" width="100%">
                          <CircularGauge score={entry.avgScore} size={32} strokeWidth={2.5} />
                        </Box>
                      ) : "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "#6b7280" }}>
                      {entry.totalCalls}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Box>
  );
}
